/**
 * PublicFooter — Footer für ALLE öffentlichen Seiten des Backtest-Tools.
 *
 * Schmaler, einheitlicher 121-Apps-Footer (identisch über alle Apps hinweg):
 * Copyright links, Rechts-Links rechts (Impressum, AGB, Datenschutz, Kontakt).
 * Die Rechtstexte werden in-app unter /legal/:slug gerendert (Frame-Lösung,
 * siehe seiten/LegalPage.js); nur der Kontakt-Link bleibt extern.
 * Über der Footerzeile steht der paperball-spezifische Anlageberatungs-
 * Disclaimer (§ 2 Abs. 2 Nr. 4 WpIG — seit dem WpIG vom 26.06.2021 ist die
 * Anlageberatung dort definiert, nicht mehr in § 1 Abs. 1a KWG).
 *
 * Darüber die Sprach-Links der Seite (Fundament-Baustein `SprachLinks`):
 * dieselbe Seite in jeder indexierbaren Sprache, als echte `<a href>` im
 * Server-HTML. Der Sprachumschalter im Kopf ist ein Knopf mit Dialog — Crawler
 * ohne JavaScript fanden darüber keine andere Sprache, und KI-Crawler folgen
 * Links statt hreflang (Kanon „Von der deutschen Seite zu allen Sprachen“).
 *
 * Das Markup steht in `LegalFooterInhalt` — dieselbe Fassung tragen die
 * Fehlerseiten von Next.js, die ohne Router und ohne Wörterbuch auskommen
 * müssen. Hier kommen Übersetzer, Router-Links und Cookie-Einstellungen dazu.
 *
 * Wird automatisch von PublicHeader gerendert wenn `withFooter`-Prop nicht
 * explizit `false` gesetzt wird — und kann auch standalone eingesetzt
 * werden (z.B. in App-internen Bereichen falls benötigt).
 */
import { Link, useLocation } from 'react-router-dom';
import { getSprachMatrix, parseLocaleFromPath, useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../hooks/useTranslation';
import { footerZiel } from '../utils/legal';
import { einzigeFassung } from '../../app/_seo/seiten';
import { SprachLinks } from '../shared/SprachLinks';
import { sprachFassungen } from '../shared/seoKopf';
import { oeffneEinwilligung } from './ConsentBanner';
import LegalFooterInhalt from './LegalFooterInhalt';

/**
 * Die Sprachfassungen der aktuellen Seite. Die Ziele kommen aus
 * `sprachFassungen` — derselben Regel, aus der `seoKopf` die hreflang-Angaben
 * baut. Ohne Matrix des Backends und auf Adressen ohne Sprach-Präfix steht
 * nichts da (`getSprachMatrix`): eine erfundene Liste wäre schlimmer als keine.
 */
function Sprachfassungen({ titel }) {
  const location = useLocation();
  // Abonniert die Sprachkonfiguration: Ist sie geladen, rendert der Block neu.
  useLanguage();
  const config = getSprachMatrix();
  const ort = parseLocaleFromPath(location.pathname);
  if (!config || !ort.isValid) return null;
  // Dieselbe Form wie in `HreflangTags`: `/de/deutsch` ohne Schrägstrich, ohne Query.
  const rest = ort.rest === '/' ? '' : ort.rest.replace(/\/+$/, '');
  return (
    <SprachLinks
      fassungen={sprachFassungen(config, rest)}
      aktuell={{ land: ort.country, sprache: ort.language }}
      titel={titel}
      darstellung="gruppen"
      className="mb-4 text-base text-slate-600"
      innenClassName=""
      titelClassName="font-semibold mb-1"
      listeClassName="flex flex-wrap gap-x-5 gap-y-1"
      linkClassName="inline-block py-1 hover:text-gray-700"
      aktuellClassName="font-semibold"
    />
  );
}

/**
 * Jede Werkzeugseite, von jeder öffentlichen Seite aus verlinkt. Bis
 * 25.10.2026 hingen Depot-Check, FIRE-Rechner und Berater-Seite an zwei bis
 * drei Seiten, der Altersvorsorgedepot-Rechner an zweien — das Menü „Für wen“
 * legte seine Links erst beim Aufklappen an. Der Text ist der Begriff, unter
 * dem die Seite gesucht wird.
 */
const WERKZEUGE = [
  { pfad: '/portfolio-backtester', key: 'footer.werkzeuge.backtest', de: 'Portfolio-Backtester' },
  { pfad: '/monte-carlo-simulation', key: 'footer.werkzeuge.monte_carlo', de: 'Monte-Carlo-Simulation' },
  { pfad: '/entnahmeplan', key: 'footer.werkzeuge.entnahmeplan', de: 'Entnahmeplan-Rechner' },
  { pfad: '/fire', key: 'footer.werkzeuge.fire', de: 'FIRE-Rechner' },
  { pfad: '/altersvorsorgedepot-rechner', key: 'footer.werkzeuge.av_depot', de: 'Altersvorsorgedepot-Rechner' },
  { pfad: '/vorabpauschale-rechner', key: 'footer.werkzeuge.vorabpauschale', de: 'Vorabpauschale-Rechner' },
  { pfad: '/depot-check', key: 'footer.werkzeuge.depot_check', de: 'Depot-Check' },
  { pfad: '/watchlist', key: 'footer.werkzeuge.watchlist', de: 'Watchlist' },
  { pfad: '/model-portfolios', key: 'footer.werkzeuge.musterportfolios', de: 'Musterportfolios' },
  { pfad: '/research/safe-withdrawal-rate', key: 'footer.werkzeuge.entnahme_studie', de: 'Studie zur Entnahmerate' },
  { pfad: '/fuer-berater', key: 'footer.werkzeuge.berater', de: 'Für Finanzberaterinnen und -berater' },
  { pfad: '/learn', key: 'footer.werkzeuge.lexikon', de: 'Kennzahlen-Lexikon' },
  { pfad: '/learn/4-percent-rule', key: 'footer.werkzeuge.vier_prozent', de: '4-Prozent-Regel' },
  { pfad: '/developers', key: 'footer.werkzeuge.developers', de: 'Entwickler & API' },
];

function Werkzeuge() {
  const { t } = useTranslation();
  const { getLocalizedPath, country, language } = useLanguage();
  // Was es nur in einer Fassung gibt (`NUR_IN`, Altersvorsorgedepot), steht nur dort.
  const eintraege = WERKZEUGE.filter((w) => {
    const einzige = einzigeFassung(w.pfad);
    return !einzige || (einzige.land === country && einzige.code === language);
  });
  return (
    <nav className="mb-4 text-base text-slate-600" aria-labelledby="footer-werkzeuge">
      <p id="footer-werkzeuge" className="font-semibold mb-1">{t('footer.werkzeuge.titel', 'Rechner & Werkzeuge')}</p>
      <ul className="flex flex-wrap gap-x-5 gap-y-1">
        {eintraege.map((w) => (
          <li key={w.pfad}>
            <Link to={getLocalizedPath(w.pfad)} className="inline-block py-1 hover:text-gray-700">{t(w.key, w.de)}</Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function PublicFooter() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <LegalFooterInhalt
      t={t}
      intern={(l, text) => <Link to={footerZiel(l, getLocalizedPath)} className="hover:text-gray-700">{text}</Link>}
      werkzeuge={<Werkzeuge />}
      cookieEinstellungen={oeffneEinwilligung}
      sprachen={<Sprachfassungen titel={t('footer.sprachfassungen', 'Diese Seite in anderen Sprachen')} />}
    />
  );
}
